import React, { useEffect, useState } from "react";
import { Search, Plus, Copy, BookOpen } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const DEFAULT_VERSIONS = ["NBLA", "NTV", "NVI", "RVR09"];

export default function BiblePanel({ onInsert }) {
  const [reference, setReference] = useState("Juan 3:16");
  const [versions, setVersions] = useState([]);
  const [selected, setSelected] = useState(DEFAULT_VERSIONS);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/bible/versions").then((r) => setVersions(r.data)).catch(() => {});
  }, []);

  const toggle = (v) => {
    setSelected((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]));
  };

  const search = async () => {
    if (!reference.trim()) return;
    setLoading(true);
    try {
      const r = await api.get("/bible/compare", {
        params: { reference: reference.trim(), versions: selected.join(",") },
      });
      setResults(r.data);
    } catch (e) {
      toast.error("No pudimos buscar el pasaje");
    } finally {
      setLoading(false);
    }
  };

  const copy = (text, ref, ver) => {
    navigator.clipboard.writeText(`${ref} (${ver}) — ${text}`);
    toast.success("Copiado");
  };

  return (
    <aside className="card-warm p-4 flex flex-col gap-3 h-full" data-testid="bible-panel">
      <div className="flex items-center gap-2">
        <BookOpen size={18} className="text-[#1F4A40]" />
        <h3 className="font-serif text-lg font-semibold text-[#13302A]">Biblia</h3>
      </div>
      <div className="flex gap-2">
        <input
          className="input-warm"
          placeholder="ej: Juan 3:16"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          data-testid="bible-reference-input"
        />
        <button className="btn-primary !min-h-10 !px-3" onClick={search} data-testid="btn-bible-search">
          <Search size={16} />
        </button>
      </div>
      <div className="flex flex-wrap gap-1">
        {versions.map((v) => (
          <button
            key={v.id}
            onClick={() => toggle(v.id)}
            className={`text-xs px-2 py-1 rounded-full border transition ${
              selected.includes(v.id)
                ? "bg-[#1F4A40] text-white border-[#1F4A40]"
                : "bg-white text-[#1F4A40] border-[rgba(31,74,64,0.2)]"
            }`}
            title={v.label}
            data-testid={`bible-ver-${v.id}`}
          >
            {v.id}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-auto flex flex-col gap-2 min-h-[200px]">
        {loading && <div className="text-sm text-[#52635C]">Buscando…</div>}
        {results.map((r, idx) => (
          <div key={idx} className="rounded-lg border border-[rgba(31,74,64,0.1)] p-3 bg-[#FBFAF7]" data-testid={`bible-result-${r.version}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="font-mono text-xs text-[#1F4A40]">{r.version} · {r.reference}</div>
              <div className="flex gap-1">
                {r.text && (
                  <>
                    <button className="btn-ghost !min-h-7 !px-2 text-xs" onClick={() => copy(r.text, r.reference, r.version)} data-testid={`btn-copy-${r.version}`}><Copy size={12} /></button>
                    <button className="btn-ghost !min-h-7 !px-2 text-xs" onClick={() => onInsert && onInsert({ reference: r.reference, version: r.version, text: r.text })} data-testid={`btn-insert-${r.version}`}><Plus size={12} /> Insertar</button>
                  </>
                )}
              </div>
            </div>
            {r.text ? (
              <p className="text-sm font-serif leading-relaxed text-[#1C2421]">{r.text}</p>
            ) : (
              <p className="text-xs italic text-[#8B9995]">{r.error || "Sin texto. Carga tu clave de API.Bible en Ajustes para desbloquear esta versión."}</p>
            )}
          </div>
        ))}
        {!loading && results.length === 0 && (
          <div className="text-sm text-[#52635C] italic">
            Buscá un pasaje (ej. "Juan 3:16") para comparar varias versiones. Mientras no cargues la clave de API.Bible en Ajustes, mostramos la Reina-Valera 1909 (dominio público).
          </div>
        )}
      </div>
    </aside>
  );
}
